// "Find details" (phase 4 §1.7, §5.6): an item without a source id — a code
// only, typed by hand, converted — is refreshed by looking its ISBN or EAN
// up. Several editions answer 409 ambiguous_source: the member picks one;
// nobody knows the code (404 source_not_found): the member searches by text.
// The pick is sent back with the refresh, which teaches the code (films).
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import { readCode } from '../../fieldTypes';
import { fieldKey } from '../../fieldKeys';
import TextSearchDialog from '../../scan/TextSearchDialog';

/** The first code an item holds in an ISBN or an EAN field: {scheme, value, key}, or null. */
export const codeOfItem = (fields, values) => {
  for (const field of fields || []) {
    if (field.type !== 'identifier' || field.unused || !['isbn', 'ean'].includes(field.scheme)) continue;
    const code = readCode(String((values || {})[fieldKey(field)] ?? ''));
    if (code) return { ...code, key: fieldKey(field) };
  }
  return null;
};

/**
 * refresh(pick?) posts refresh-from-source and calls onDone(answer); a 409
 * or a 404 opens the dialog it returns (`dialog`), whose pick refreshes
 * again with {source, source_id}. Other failures go to onError(error).
 */
export function useFindDetails({ inventoryId, itemId, itemType, code, lang, onDone, onError }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(null);   // {candidates?} — the dialog is open
  const refresh = async (pick) => {
    setBusy(true);
    try {
      const response = await api.post(`/inventories/${inventoryId}/items/${itemId}/refresh-from-source`,
        pick ? { source: pick.source, source_id: pick.source_id, ticket: asking && asking.ticket } : {});
      setAsking(null);
      onDone(response.data);
    } catch (error) {
      const data = (error.response && error.response.data) || {};
      // The answer's ticket goes back with the pick: it proves the pick follows this lookup (and teaches the code).
      if (!pick && code && data.code === 'ambiguous_source') setAsking({ candidates: data.candidates || [], ticket: data.ticket });
      else if (!pick && code && data.code === 'source_not_found' && !data.source) setAsking({ ticket: data.ticket });
      else onError(error);
    } finally {
      setBusy(false);
    }
  };
  const dialog = asking ? (
    <TextSearchDialog itemType={itemType} code={code} lang={lang} candidates={asking.candidates}
      title={asking.candidates ? t('findDetails.pickTitle') : t('findDetails.searchTitle')}
      onClose={() => setAsking(null)} onPick={(candidate) => refresh(candidate)} />
  ) : null;
  return { refresh: () => refresh(null), busy, dialog };
}
